import React, { useCallback, useEffect, useState } from "react";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro, Card, PlaceholderState } from "@/admin/ui";
import { Cake, RefreshCw, Loader2, Info, CalendarRange } from "lucide-react";

const MONTHS = ["", "January", "February", "March", "April", "May", "June", "July",
  "August", "September", "October", "November", "December"];

export default function Birthdays() {
  const { event } = useAdminEvent();
  const eventId = event?.id;
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    if (!eventId) return;
    setState("loading");
    api.mgrBirthdays(eventId)
      .then((d) => { setData(d); setState("ok"); })
      .catch(() => setState("error"));
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  if (state === "loading") {
    return <div className="premium-card p-10 grid place-items-center" data-testid="birthdays-loading"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--secondary))]" /></div>;
  }
  if (state === "error") {
    return (
      <Card className="text-center" >
        <p className="text-[hsl(var(--muted-foreground))]">We couldn't load birthdays.</p>
        <button onClick={load} data-testid="birthdays-retry" className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold"><RefreshCw className="h-4 w-4" /> Retry</button>
      </Card>
    );
  }

  const rows = data?.birthdays || [];
  const dates = data?.event_dates || {};

  return (
    <div data-testid="birthdays-page">
      <PageIntro>
        Attendees who opted in to birthday recognition AND whose birthday falls within the
        event dates. Birthdays are never shown to other attendees or included in general exports.
      </PageIntro>

      <div className="flex items-center gap-2 mb-4 text-sm text-[hsl(var(--muted-foreground))]">
        <CalendarRange className="h-4 w-4 text-[hsl(var(--secondary))]" />
        <span data-testid="birthdays-event-window">
          Event window: {dates.start || "—"} to {dates.end || "—"}
        </span>
      </div>

      {rows.length === 0 ? (
        <PlaceholderState icon={Cake} title="No birthdays during the event"
          description="Nobody who opted in has a birthday within the configured event dates yet." />
      ) : (
        <div className="premium-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-[hsl(var(--border))]">
                <th className="px-4 py-3 font-semibold">Attendee</th>
                <th className="px-4 py-3 font-semibold">Birthday</th>
                <th className="px-4 py-3 font-semibold">Opted in</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.attendee_id} data-testid="birthday-row" className="border-b border-[hsl(var(--border))] last:border-0">
                  <td className="px-4 py-3 font-medium text-[hsl(var(--primary))]">{r.display_name}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--pc-gold)/0.15)] text-[hsl(38_45%_32%)] px-2.5 py-1 font-semibold">
                      <Cake className="h-3.5 w-3.5" /> {MONTHS[r.birthday_month]} {r.birthday_day}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[hsl(var(--muted-foreground))] text-xs">{r.consent_at ? new Date(r.consent_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(data?.excluded_outside_event_window || 0) > 0 && (
        <p className="mt-3 text-xs text-[hsl(var(--muted-foreground))]" data-testid="birthdays-excluded-note">
          {data.excluded_outside_event_window} opted-in birthday(s) fall outside the event window and are hidden here.
        </p>
      )}

      <div className="mt-4 flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <p>{data?.note}</p>
      </div>
    </div>
  );
}
